import React from 'react';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useDeepCompareEffect } from '@fuse/hooks';

import { executeStored } from 'app/main/components/Utils';
import { type GridListProps } from 'app/main/components/GridList';
import { type FiltersProps } from 'app/main/components/hooks/useFilters';
import { type SelectionProps } from 'app/main/components/hooks/useSelection';
import { type PaginationType } from 'app/main/components/hooks/usePagination';
import useSettings from 'app/main/components/hooks/useSettings';
import useCancelablePromise from 'app/main/components/hooks/useCancelablePromise';
import GridList from 'app/main/components/GridList';
import ErrorAlert from 'app/main/components/ErrorAlert';

/* eslint-disable-next-line import/no-cycle */
import useViewState from '../components/useViewState';
import ViewPagination from '../pagination/ViewPagination';
import ViewFooter, { type ViewFooterCustomInfoProps } from '../components/ViewFooter';

export interface ViewListProps {
	className?: string;
	id?: string;
	module: string;
	storedProcedure?: string;
	itemLayout: GridListProps['itemLayout'];
	defaultFilters?: FiltersProps['defaultFilters'];
	defaultQuery?: any;
	filters?: FiltersProps['filters'];
	paginationProps?: PaginationType;
	refresh?: boolean;
	updateRefresh?: VoidFunction;
	defaultNRows?: number;
	selectionProps?: SelectionProps;
	slots?: {
		footerCustomInfo?: (props: ViewFooterCustomInfoProps) => React.ReactNode;
	};
	showBottomPagination?: boolean;
	showFilters?: boolean;
	showExportButton?: boolean;
	showOrder?: boolean;
}

export default function ViewList({
	defaultNRows = 30,
	showBottomPagination = true,
	showFilters = true,
	showExportButton = true,
	showOrder = true,
	...props
}: ViewListProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(props.module);
	const getCancelablePromise = useCancelablePromise();

	const [items, setItems] = React.useState([]);
	const [additionalInfo, setAdditionalInfo] = React.useState<any>({});
	const [error, setError] = React.useState<any>();
	const [refresh, setRefresh] = React.useState(true);

	const updateRefresh = () => {
		setRefresh(value => !value);
		setError(null);
	};

	const viewState = useViewState({
		id: props.id,
		module: props.module,
		filter: {
			defaultFilters: props.defaultFilters,
			defaultQuery: props.defaultQuery,
			filters: props.filters
		},
		paginator: { defaultNRows }
	});

	const paginationProps = props.paginationProps || viewState.paginationProps;

	const { filterProps, orderProps, paginatorProps } = paginationProps;
	const { nPage, nRows, setMaxRows, isLoading, setIsLoading } = paginatorProps;

	useDeepCompareEffect(() => {
		setIsLoading(true);
		executeStored({
			action:
				props.storedProcedure ||
				(!settings.modu_systemtable
					? `${settings.modu_external ? 'ext_' : ''}st_${props.module}_list`
					: 'st_sys_systable_list'),
			params: {
				n_page: nPage,
				n_rows: nRows,
				systable: settings.modu_systemtable ? props.module : undefined,
				filters: filterProps?.filters,
				query: filterProps?.query,
				order_by: orderProps?.orderString
			},
			dispatch,
			getCancelablePromise,
			successHandler: response => {
				const { n_record, list, ...other } = response.data;
				setMaxRows(n_record);
				setItems(list || []);
				setAdditionalInfo(other);
				setError(null);
				setIsLoading(false);
			},
			errorHandler: ex => {
				setError(ex);
				setIsLoading(false);
			}
		});
	}, [
		dispatch,
		filterProps?.query,
		filterProps?.filters,
		nPage,
		nRows,
		orderProps?.orderBy,
		orderProps?.orderOperator,
		props.module,
		props.refresh,
		props.storedProcedure,
		refresh,
		setMaxRows
	]);

	const footerCustomInfo = props.slots?.footerCustomInfo
		? props.slots?.footerCustomInfo({ items, selectedItems: props.selectionProps?.selectedItems, additionalInfo })
		: undefined;

	return (
		<>
			<ViewPagination
				className="sticky top-0 z-99"
				module={props.module}
				paginationProps={paginationProps}
				updateRefresh={props.updateRefresh || updateRefresh}
				showFilters={showFilters}
				showExportButton={showExportButton}
				showOrder={showOrder}
				showBorderBottom
			/>
			<Box
				className={clsx('h-full overflow-y-auto', props.className)}
				sx={theme => ({ borderBottom: `1px solid ${theme.palette.divider}` })}
			>
				{isLoading || !settings ? (
					<LinearProgress variant="query" />
				) : error ? (
					<ErrorAlert error={error} />
				) : items.length === 0 ? (
					<Typography className="p-16 italic" align="center" color="textSecondary">
						{t('LIST_NO_ITEMS')}
					</Typography>
				) : (
					<GridList
						settings={settings}
						items={items}
						itemLayout={props.itemLayout}
						filterProps={paginationProps.filterProps}
						updateRefresh={props.updateRefresh || updateRefresh}
						selectionProps={props.selectionProps}
					/>
				)}
			</Box>
			{showBottomPagination ? (
				<ViewFooter paginatorProps={paginatorProps} customInfo={footerCustomInfo} />
			) : (
				<></>
			)}
		</>
	);
}
